// uploadProposal.jsx
import FileUpload from "components/FileUpload";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateLegalTrainingProvision } from "services/legalTrainingProvision";
import { updateLegislativeDrafting } from "services/legislativeService";
import { updateSectorDirective } from "services/sectorDirectives";
import { LEGAL_ADVISORY_REQUEST_STATUS, LEGAL_TRAINING_PROVISION_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { useTranslation } from "react-i18next";

interface uploadProposalProps {
    request: any;
    isOpen: boolean;
    onClose: () => void;
    setLegalResearch: Function;
    type?: string;
    onUploadProposal: (data: {
        proposalSummary: string;
        proposalFile: string;
    }) => void;
}

const UploadDraftLaw: React.FC<uploadProposalProps> = ({
    request,
    isOpen,
    setLegalResearch,
    onUploadProposal,
    onClose,
    type,
}) => {
    const { t } = useTranslation();
    const [proposalSummary, setProposalSummary] = useState(() => {
        if (type === "training_manual") {
            return request.summary || "";
        } else if (type === "training_outcome") {
            return request.outcome_of_training || "";
        } else if (type === "training_feedback") {
            return request.feedback_from_trainee || "";
        }
        return request.proposal_summary || "";
    });
    const [proposalFile, setProposalFile] = useState<File | null>(null);
    const contextData = useAuth();
    const { showToast } = useToast();
    const [submitting, setSubmitting] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Create a ref for the modal content div
    const modalContentRef = useRef<HTMLDivElement>(null);

    if (!isOpen) return null;

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setLegalResearch((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setValidationError(null);
        setSubmitting(true);

        try {
            const data = new FormData();
            let response: any;
            if (type === "training_outcome") {
                data.append("status", LEGAL_TRAINING_PROVISION_REQUEST_STATUS.final.value);
                data.append("outcome_of_training", proposalSummary);
                if (proposalFile) {
                    data.append("outcome_training_files", proposalFile);
                }
            }
            else if (type === "training_feedback") {
                data.append("feedback_from_trainee", proposalSummary);
                if (proposalFile) {
                    data.append("feedback_files", proposalFile);
                }
            } else {
                data.append("status", LEGAL_TRAINING_PROVISION_REQUEST_STATUS.training_manual_submitted.value);
                data.append("summary", proposalSummary);
                if (proposalFile) {
                    data.append("training_manual_files", proposalFile);
                }
            }
            response = await updateLegalTrainingProvision(request.id, data);
            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("TrainingManualUploaded") || "Training Manual Uploaded", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUploadTrainingManual") || "Failed to upload Training Manual.", "error");
        } finally {
            setSubmitting(false);
        }

        onUploadProposal({ proposalSummary, proposalFile: proposalFile ? proposalFile.name : "" });
        onClose();
    };

    const handleFileChange = (
        file: File | null,
    ) => {
        setProposalFile(file);
    };

    // Get title based on type
    const getTitle = () => {
        if (type === "training_outcome") {
            return t("TrainingOutcome") || "Training Outcome";
        } else if (type === "training_feedback") {
            return t("TrainingFeedback") || "Training Feedback";
        }
        return t("TrainingManual") || "Training Manual";
    };

    // Get label based on type
    const getLabel = () => {
        if (type === "training_outcome") {
            return t("SummaryOfTrainingOutcome") || "Summary of Training Outcome";
        } else if (type === "training_feedback") {
            return t("SummaryOfFeedback") || "Summary of Feedback";
        }
        return t("SummaryOfTrainingManual") || "Summary of Training Manual";
    };

    // Get file label based on type
    const getFileLabel = () => {
        if (type === "training_outcome") {
            return t("TrainingOutcomeDoc") || "Training Outcome Doc*";
        } else if (type === "training_feedback") {
            return t("FeedbackDoc") || "Feedback Doc*";
        }
        return t("TrainingManualDoc") || "Training Manual Doc*";
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-end p-4 z-50 overflow-hidden">
            <div
                ref={modalContentRef}
                className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6 relative flex flex-col h-full my-auto overflow-y-auto pr-2 custom-scrollbar"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {getTitle()}
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col">
                    <div className="flex-grow overflow-y-auto pr-2 custom-scrollbar">
                        <div className="mb-4">
                            <label
                                htmlFor="remark"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {getLabel()}
                            </label>
                            <textarea
                                id="remark"
                                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-y min-h-[100px]"
                                placeholder={t("DetailHere") || "detail here..."}
                                value={proposalSummary}
                                onChange={(e) => setProposalSummary(e.target.value)}
                            ></textarea>
                        </div>

                        <div className="mb-6">
                            <label
                                htmlFor="deadline"
                                className="block text-sm font-medium text-gray-700 mb-2"
                            >
                                {getFileLabel()}
                            </label>
                            <div className="relative">
                                <FileUpload onChange={handleFileChange} />
                            </div>
                        </div>
                    </div>
                    {validationError && (
                        <p className="text-red-600 text-sm mb-4">{validationError}</p>
                    )}
                    <div className="flex space-x-4 pt-4 border-t border-gray-200">
                        <button
                            type="submit"
                            className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-3 px-6 rounded-md disabled:opacity-70"
                            disabled={submitting}
                        >
                            {submitting ? (t("Submitting") || "Submitting...") : (t("Submit") || "Submit")}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default UploadDraftLaw;